class PMCustomStickersOrderSummary extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.currency = this.dataset.currency || 'EUR';
    this.productInfo = document.getElementById(`ProductInfo-${this.sectionId}`);
    this.priceRoot = document.getElementById(`price-${this.sectionId}`);
    this.values = {
      shape: this.querySelector('[data-pm-summary-value="shape"]'),
      size: this.querySelector('[data-pm-summary-value="size"]'),
      quantity: this.querySelector('[data-pm-summary-value="quantity"]'),
      finish: this.querySelector('[data-pm-summary-value="finish"]'),
      unitPrice: this.querySelector('[data-pm-summary-value="unit-price"]'),
      totalPrice: this.querySelector('[data-pm-summary-value="total-price"]'),
    };

    this.optionLabels = {
      shape: ['shape'],
      size: ['size', 'sticker size', 'width', 'height', 'dimensions'],
      quantity: ['quantity', 'qty', 'amount', 'pack', 'pieces', 'pcs'],
      finish: ['finish', 'material', 'lamination', 'laminate', 'coating', 'vinyl'],
    };

    this.uploadLabels = ['upload', 'artwork', 'logo'];
    this.onChange = () => {
      this.clearLpoPriceCache();
      this.scheduleUpdate();
      this.scheduleDelayedUpdate();
    };
    this.onLpoPrice = (event) => this.handleLpoPrice(event);
    this.onMutation = () => this.scheduleUpdate();

    document.addEventListener('change', this.onChange, true);
    document.addEventListener('input', this.onChange, true);
    document.addEventListener('click', this.onChange, true);
    document.addEventListener('lpo:price', this.onLpoPrice);

    if (this.productInfo) {
      this.productObserver = new MutationObserver(this.onMutation);
      this.productObserver.observe(this.productInfo, {
        attributes: true,
        attributeFilter: ['class', 'aria-checked', 'aria-selected', 'aria-pressed', 'checked', 'selected'],
        childList: true,
        subtree: true,
      });
    }

    if (this.priceRoot) {
      this.priceObserver = new MutationObserver(this.onMutation);
      this.priceObserver.observe(this.priceRoot, { childList: true, subtree: true, characterData: true });
    }

    this.observeStickyPrice();
    this.scheduleUpdate();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onChange, true);
    document.removeEventListener('input', this.onChange, true);
    document.removeEventListener('click', this.onChange, true);
    document.removeEventListener('lpo:price', this.onLpoPrice);
    this.productObserver?.disconnect();
    this.priceObserver?.disconnect();
    this.stickyPriceObserver?.disconnect();
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    window.clearTimeout(this.delayedUpdateId);
    window.clearTimeout(this.finalUpdateId);
  }

  handleLpoPrice(event) {
    const price = event?.detail?.price;

    if (typeof price === 'string' && price.trim()) {
      this.lastLpoPriceText = price.trim();
      this.lastLpoPriceNumber = this.parseMoney(this.lastLpoPriceText);
    } else if (typeof price === 'number' && Number.isFinite(price)) {
      this.lastLpoPriceNumber = price > 999 ? price / 100 : price;
      this.lastLpoPriceText = this.formatCurrency(this.lastLpoPriceNumber);
    }

    this.scheduleUpdate();
    this.scheduleDelayedUpdate();
  }

  scheduleUpdate() {
    if (this.rafId) return;

    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = null;
      this.placeSummary();
      this.updateSummary();
    });
  }

  scheduleDelayedUpdate() {
    window.clearTimeout(this.delayedUpdateId);
    window.clearTimeout(this.finalUpdateId);

    this.delayedUpdateId = window.setTimeout(() => {
      this.clearLpoPriceCache();
      this.scheduleUpdate();
    }, 350);

    this.finalUpdateId = window.setTimeout(() => {
      this.clearLpoPriceCache();
      this.scheduleUpdate();
    }, 900);
  }

  observeStickyPrice() {
    const stickyPrice = document.querySelector('[data-pm-sticky-price]');
    if (!stickyPrice || this.stickyPriceObserver) return;

    this.stickyPriceObserver = new MutationObserver(this.onMutation);
    this.stickyPriceObserver.observe(stickyPrice, { childList: true, subtree: true, characterData: true });
  }

  clearLpoPriceCache() {
    this.lastLpoPriceText = '';
    this.lastLpoPriceNumber = null;
  }

  placeSummary() {
    if (!this.productInfo || this.hasAttribute('data-pm-placed')) return;

    const uploadBlock = this.findUploadBlock();
    if (uploadBlock?.parentElement) {
      uploadBlock.parentElement.insertBefore(this, uploadBlock);
      this.removeAttribute('hidden');
      this.setAttribute('data-pm-placed', 'true');
      return;
    }

    const priceBlock = this.priceRoot;
    if (priceBlock?.parentElement) {
      priceBlock.parentElement.insertBefore(this, priceBlock);
      this.removeAttribute('hidden');
      this.setAttribute('data-pm-placed', 'true');
    }
  }

  updateSummary() {
  if (!this.productInfo) return;

  const shape = this.readOptionValue('shape');
  const size = this.readOptionValue('size');
  let quantityText = this.readOptionValue('quantity');
  const finish = this.readOptionValue('finish');
  const totalText = this.readTotalPriceText();
  const totalNumber = this.parseMoney(totalText);

  if (/custom/i.test(quantityText)) {
    const customSmall = document.querySelector('input[name="properties[custom_quantity_small]"]')?.value;
    const customLarge = document.querySelector('input[name="properties[custom_quantity_large]"]')?.value;
    const customQty = customSmall || customLarge;
    const qty = this.parseQuantity(customQty);

    if (qty) {
      quantityText = `${qty} stickers`;
    }
  } else {
    const qty = this.parseQuantity(quantityText);

    if (qty) {
      quantityText = `${qty} stickers`;
    }
  }

  const quantityNumber = this.parseQuantity(quantityText);

  this.setValue('shape', shape);
  this.setValue('size', size);
  this.setValue('quantity', quantityText);
  this.setValue('finish', finish);
  this.setValue('totalPrice', totalText);

  if (totalNumber && quantityNumber) {
    this.setValue('unitPrice', this.formatCurrency(totalNumber / quantityNumber));
  } else {
    this.setValue('unitPrice', '-');
  }
}

  setValue(key, value) {
    if (!this.values[key]) return;

    this.values[key].textContent = value || '-';
  }

  readOptionValue(key) {
    const cloudliftValue = this.readCloudliftOptionValue(key);
    if (cloudliftValue) return cloudliftValue;

    const formPropertyValue = this.readFormPropertyValue(key);
    if (formPropertyValue) return formPropertyValue;

    const labelElement = this.findOptionLabel(this.optionLabels[key]);
    if (!labelElement) return '-';

    const group = this.findOptionGroup(labelElement);
    if (!group) return '-';

    return this.extractSelectedValue(group, labelElement) || '-';
  }

  readCloudliftOptionValue(key) {
    const group = this.findCloudliftOptionGroup(key);
    if (!group) return '';

    return this.extractSelectedValue(group, null) || this.readCloudliftTextValue(group);
  }

  findCloudliftOptionGroup(key) {
    if (!this.productInfo) return null;

    const aliases = this.optionLabels[key].map((alias) => this.normalize(alias));
    const groups = this.productInfo.querySelectorAll('.cl-po--option[data-option], [data-option], [data-option-name]');
    const matches = Array.from(groups)
      .filter((group) => {
        if (this.contains(group)) return false;

        const optionName = this.getOptionName(group);
        if (!optionName || this.matchesAny(optionName, this.uploadLabels)) return false;
        if (key !== 'quantity' && optionName.includes('discount')) return false;

        return aliases.some((alias) => optionName === alias || optionName.includes(alias) || alias.includes(optionName));
      })
      .sort((a, b) => this.scoreOptionGroup(b, aliases, key) - this.scoreOptionGroup(a, aliases, key));

    return matches[0] || null;
  }

  getOptionName(group) {
    const explicitName = group.getAttribute('data-option') || group.getAttribute('data-option-name');
    const labelText = group.querySelector('.cl-po--label, label, legend, [class*="label" i]')?.textContent;

    return this.normalize(explicitName || labelText || '');
  }

  scoreOptionGroup(group, aliases, key) {
    const optionName = this.getOptionName(group);
    let score = 0;

    if (aliases.some((alias) => optionName === alias)) score += 100;
    if (aliases.some((alias) => optionName.startsWith(alias))) score += 50;
    if (aliases.some((alias) => optionName.includes(alias))) score += 25;
    if (group.matches('.cl-po--option')) score += 10;
    if (key === 'quantity' && optionName === 'quantity') score += 100;
    if (optionName.includes('discount')) score -= 90;

    return score;
  }

  readCloudliftTextValue(group) {
    const valueNode = group.querySelector(
      '.cl-po--value, .cl-po--selected-value, [class*="selected-value" i], [data-selected-value], [data-value]'
    );
    const value =
      valueNode?.getAttribute('data-selected-value') ||
      valueNode?.getAttribute('data-value') ||
      valueNode?.textContent ||
      '';

    return this.cleanValue(value, group.querySelector('.cl-po--label, label, legend'));
  }

  readFormPropertyValue(key) {
    const form = this.productInfo?.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    if (!form) return '';

    const aliases = this.optionLabels[key].map((alias) => this.normalize(alias));
    const controls = form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');

    return (
      Array.from(controls)
        .map((control) => {
          const name = this.normalize(control.getAttribute('name'));
          const matchesName = aliases.some((alias) => name.includes(alias));
          if (!matchesName || control.type === 'file') return '';
          if ((control.type === 'radio' || control.type === 'checkbox') && !control.checked) return '';

          if (control.tagName === 'SELECT') {
            return this.cleanValue(control.selectedOptions?.[0]?.textContent || control.value, null);
          }

          return this.cleanValue(control.value, null);
        })
        .find(Boolean) || ''
    );
  }

  findOptionLabel(aliases) {
    if (!this.productInfo) return null;

    const candidates = this.productInfo.querySelectorAll(
      'label, legend, [class*="label" i], [class*="title" i], [class*="name" i], [data-label], [data-name]'
    );

    return Array.from(candidates).find((candidate) => {
      if (this.contains(candidate)) return false;

      const text = this.normalize(candidate.textContent);
      if (!text || this.matchesAny(text, this.uploadLabels)) return false;

      return aliases.some((alias) => this.isFieldLabel(text, alias));
    });
  }

  isFieldLabel(text, alias) {
    const normalizedAlias = this.normalize(alias);

    return text === normalizedAlias || text.startsWith(`${normalizedAlias}:`) || text.startsWith(`${normalizedAlias} `);
  }

  findOptionGroup(labelElement) {
    let current = labelElement;

    while (current && current !== this.productInfo) {
      if (
        current.matches?.(
          'fieldset, [class*="lpo-option" i], [class*="product-option" i], [class*="option" i], [data-option], [data-option-name], .product-form__input'
        )
      ) {
        return current;
      }

      current = current.parentElement;
    }

    return labelElement.parentElement;
  }

  extractSelectedValue(group, labelElement) {
    const select = group.querySelector('select');
    if (select?.selectedOptions?.[0]) {
      return this.cleanValue(select.selectedOptions[0].textContent, labelElement);
    }

    const checkedInput = group.querySelector('input:checked');
    if (checkedInput) {
      return this.cleanValue(this.getInputLabelText(checkedInput), labelElement);
    }

    const quantityInput = group.querySelector('.cl-po--quantity, input[type="number"], input[inputmode="numeric"]');
    if (quantityInput?.value?.trim()) {
      return this.cleanValue(quantityInput.value, labelElement);
    }

    const activeValue = this.findActiveValue(group);
    if (activeValue) {
      return this.cleanValue(activeValue, labelElement);
    }

    const filledInput = Array.from(group.querySelectorAll('input:not([type="hidden"]):not([type="file"]), textarea')).find(
      (input) => input.value && input.value.trim()
    );
    if (filledInput) {
      return this.cleanValue(filledInput.value, labelElement);
    }

    return '';
  }

  findActiveValue(group) {
    const activeSelectors = [
      'button[aria-pressed="true"]',
      'button[aria-selected="true"]',
      '[role="button"][aria-pressed="true"]',
      '[role="option"][aria-selected="true"]',
      '.cl-po--radio-item[class*="selected" i]',
      '.cl-po--radio-item[class*="active" i]',
      '.cl-po--swatch[class*="selected" i]',
      '.cl-po--swatch[class*="active" i]',
      '[class*="selected" i]',
      '[class*="active" i]',
    ];
    const activeElement = Array.from(group.querySelectorAll(activeSelectors.join(','))).find((element) => {
      if (element === group) return false;
      if (element.matches('.cl-po--label, [class*="label" i]')) return false;

      return this.cleanValue(element.textContent, null);
    });

    return activeElement?.getAttribute('data-value') || activeElement?.textContent || '';
  }

  getInputLabelText(input) {
    const explicitLabel = input.id ? this.productInfo.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
    const wrappingLabel = input.closest('label');
    const cloudliftItem = input.closest('.cl-po--radio-item, .cl-po--swatch, [class*="radio-item" i], [class*="option-value" i]');
    const ariaLabel = input.getAttribute('aria-label');

    return (
      explicitLabel?.textContent ||
      wrappingLabel?.textContent ||
      cloudliftItem?.textContent ||
      ariaLabel ||
      (input.value && !['on', 'true', 'false'].includes(input.value.toLowerCase()) ? input.value : '')
    );
  }

  cleanValue(value, labelElement) {
    const label = this.normalize(labelElement?.textContent || '');
    let cleaned = (value || '').replace(/\s+/g, ' ').trim();

    if (!cleaned) return '';

    if (label && this.normalize(cleaned).startsWith(label)) {
      cleaned = cleaned.slice(labelElement.textContent.trim().length).replace(/^[:\s-]+/, '').trim();
    }

    cleaned = cleaned.replace(/\s*\+\s*[€$£]\s*\d+(?:[.,]\d{1,2})?.*$/i, '').trim();

    return this.dedupeRepeatedValue(cleaned);
  }

  dedupeRepeatedValue(value) {
    const parts = String(value || '').split(/\s+/).filter(Boolean);
    if (parts.length > 1 && parts.length % 2 === 0) {
      const midpoint = parts.length / 2;
      const firstHalf = parts.slice(0, midpoint).join(' ');
      const secondHalf = parts.slice(midpoint).join(' ');

      if (this.normalize(firstHalf) === this.normalize(secondHalf)) {
        return firstHalf;
      }
    }

    return value;
  }

  findUploadBlock() {
    if (!this.productInfo) return null;

    const candidates = this.productInfo.querySelectorAll(
      'label, legend, [class*="upload" i], [class*="label" i], [class*="title" i], [data-label], [data-name]'
    );
    const label = Array.from(candidates).find((candidate) => {
      if (this.contains(candidate)) return false;
      const text = this.normalize(candidate.textContent || candidate.getAttribute('data-label') || candidate.getAttribute('data-name'));

      return text && this.matchesAny(text, this.uploadLabels);
    });

    if (!label) return null;

    return this.findUploadGroup(label);
  }

  findUploadGroup(labelElement) {
    let current = labelElement;

    while (current && current !== this.productInfo) {
      if (
        current.matches?.(
          'fieldset, [class*="upload" i], [class*="lpo-option" i], [class*="product-option" i], [data-option], [data-option-name], .product-form__input, .shopify-app-block'
        )
      ) {
        if (current.matches('.shopify-app-block') && this.containsCoreOptionLabels(current)) {
          return labelElement.parentElement;
        }

        return current;
      }

      current = current.parentElement;
    }

    return labelElement.parentElement;
  }

  containsCoreOptionLabels(element) {
    const text = this.normalize(element?.textContent);

    return ['shape', 'size', 'quantity', 'finish'].some((label) => text.includes(label));
  }

  readTotalPriceText() {
    const displayedPrice = this.getCurrentDisplayedPriceText();

    return displayedPrice || this.lastLpoPriceText || '-';
  }

  getCurrentDisplayedPriceText() {
    const selectors = [
      '[data-pm-sticky-price]',
      '.cl-po--total-price',
      '.cl-po--price-total',
      '[class*="total" i][class*="price" i]',
      '[data-live-price-quantity] .price-item--sale',
      '[data-live-price-quantity] .price-item--regular',
      '.price-item--sale',
      '.price-item--regular',
    ];

    for (const selector of selectors) {
      const nodes = Array.from(document.querySelectorAll(selector)).filter((node) => {
        if (this.contains(node)) return false;
        if (node.closest('pm-custom-stickers-order-summary')) return false;

        return this.parseMoney(node.textContent) !== null;
      });
      const text = nodes[nodes.length - 1]?.textContent?.trim();

      if (text) return text;
    }

    return '';
  }

  parseQuantity(value) {
    const match = String(value || '').match(/\d+(?:[.,]\d+)?/);
    if (!match) return null;

    const quantity = Number(match[0].replace(',', '.'));

    return Number.isFinite(quantity) && quantity > 0 ? quantity : null;
  }

  parseMoney(value) {
    if (this.lastLpoPriceText && value === this.lastLpoPriceText && this.lastLpoPriceNumber) {
      return this.lastLpoPriceNumber;
    }

    const money = String(value || '').replace(/[^0-9,.-]/g, '');
    const lastDot = money.lastIndexOf('.');
    const lastComma = money.lastIndexOf(',');
    const decimalIndex = Math.max(lastDot, lastComma);
    const normalized =
      decimalIndex > -1
        ? `${money.slice(0, decimalIndex).replace(/[.,]/g, '')}.${money.slice(decimalIndex + 1).replace(/[.,]/g, '')}`
        : money.replace(/[.,]/g, '');
    const amount = Number(normalized);

    return Number.isFinite(amount) && amount > 0 ? amount : null;
  }

  formatCurrency(amount) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      return `€${amount.toFixed(2)}`;
    }
  }

  normalize(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  matchesAny(text, aliases) {
    return aliases.some((alias) => text.includes(this.normalize(alias)));
  }
}

if (!customElements.get('pm-custom-stickers-order-summary')) {
  customElements.define('pm-custom-stickers-order-summary', PMCustomStickersOrderSummary);
}
